import React, { useEffect, useState } from "react";
import CreatePostService from "./CreatePostService";
import { Outlet, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";
import { getDomain } from "../../../services/postService.services";

function CareerCounselingHeader() {
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedFields, setSelectedFields] = useState([]); // Array for multi-select
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [careerFieldsOptions, setCareerFieldsOptions] = useState([]);
  const [edit, setEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  const handleNavigation = () => {
    navigate(`/admin/manage-preloaded-details`);
  };

  const customers = useSelector((state) => state.customer.customers);
  const customersWithCareerCounseling = customers.filter((customer) =>
    customer.services.some(
      (service) => service.coreservice_name === "Career Counseling"
    )
  );

  // Handle multi-select changes
  const handleFieldChange = (selectedOptions) => {
    setSelectedFields(selectedOptions || []);
  };

  const isCreatePageDisabled = !selectedStudent || selectedFields.length === 0;

  const handleCreatePage = () => {
    setIsModalOpen(true);
  };

  const getDomains = async () => {
    setIsLoading(true);
    try {
      const res = await getDomain();

      if (res && res.data) {
        const formattedOptions = res.data.map((item) => ({
          value: item.id,
          label: item.domain_name,
        }));

        setCareerFieldsOptions(formattedOptions);
      }
    } catch (error) {
      console.error("Error fetching domains:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDomains();
  }, []);

  // Custom styles for React-Select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "0.5rem",
      borderColor: "#E2E8F0",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#805AD5",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#EDE9FE",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#6B46C1",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6B46C1",
      "&:hover": {
        backgroundColor: "#9F7AEA",
        color: "white",
      },
    }),
  };

  return (
    <div className="p-6 max-w-full mx-auto bg-white rounded-xl shadow-sm">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 border-b pb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Career Counseling
          </h1>
          <p className="text-gray-600">
            Create and manage career guidance for students
          </p>
        </div>
        <button
          onClick={handleNavigation}
          className="mt-4 sm:mt-0 bg-purple-700 text-white py-2 px-6 rounded-lg hover:bg-purple-800 transition duration-200 flex items-center gap-2 shadow-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
          Manage Preloaded Details
        </button>
      </div>

      {/* Form Section */}
      <div className="bg-purple-50 p-6 rounded-xl border border-purple-100 shadow-inner mb-2">
        <h2 className="text-xl font-semibold text-purple-800 mb-6">
          Create New Career Guidance
        </h2>

        <div className="flex flex-col lg:flex-row lg:items-end gap-6">
          {/* Select a Student */}
          <div className="flex flex-col flex-1">
            <label className="text-gray-700 font-medium mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-purple-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
              Select a Student
            </label>
            <select
              value={selectedStudent?.id || ""}
              onChange={(e) => {
                const student = customersWithCareerCounseling.find(
                  (student) => student.id === Number(e.target.value)
                );
                setSelectedStudent(student || "");
              }}
              className="py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent bg-white shadow-sm"
            >
              <option value="">Choose a student</option>
              {customersWithCareerCounseling.length === 0 ? (
                <option disabled>No students available</option>
              ) : (
                customersWithCareerCounseling.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Select the Career Field (Multi-select with React-Select) */}
          <div className="flex flex-col flex-1">
            <label className="text-gray-700 font-medium mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-purple-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
              </svg>
              Select Career Fields
            </label>
            {isLoading ? (
              <div className="py-3 px-4 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                <svg
                  className="animate-spin h-5 w-5 text-purple-600 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Loading career fields...
              </div>
            ) : (
              <Select
                options={careerFieldsOptions}
                isMulti
                value={selectedFields}
                onChange={handleFieldChange}
                placeholder="Select one or more fields"
                className="basic-multi-select z-50"
                classNamePrefix="select"
                styles={customSelectStyles}
                noOptionsMessage={() => "No career fields available"}
              />
            )}
          </div>

          {/* Create Page Button */}
          <button
            onClick={handleCreatePage}
            disabled={isCreatePageDisabled}
            className={`py-3 px-8 rounded-lg flex items-center justify-center transition duration-200 shadow-md ${
              isCreatePageDisabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-purple-700 text-white hover:bg-purple-800 transform hover:-translate-y-1"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Create Page
          </button>
        </div>

        {isCreatePageDisabled && (
          <div className="mt-4 text-sm text-gray-600 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2 text-amber-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Please select both a student and at least one career field to create
            a page
          </div>
        )}
      </div>

      {/* Recent Pages Section - Could be added here */}
      {/* <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          Recent Pages
        </h2>
        <p className="text-gray-500 mb-4">
          Your most recently created career guidance pages will appear here
        </p>
        <div className="flex items-center justify-center py-8 text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p>No recent pages to display</p>
        </div>
      </div> */}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl h-screen overflow-auto p-6 relative">
            {/* Close button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Render the CreatePostService Component */}
            <CreatePostService
              setIsModalOpen={setIsModalOpen}
              selectedStudent={selectedStudent}
              selectedFields={selectedFields}
              edit={false}
              setEdit={setEdit}
            />
          </div>
        </div>
      )}

      <Outlet />
    </div>
  );
}

export default CareerCounselingHeader;
